import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { resourcesApi } from '../services/api';
import { Resource } from '../types';

const Resources: React.FC = () => {
  const { user } = useAuth();
  const [resources, setResources] = useState<Resource[]>([]);
  const [filteredResources, setFilteredResources] = useState<Resource[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const resourcesData = await resourcesApi.getAll();

        // Extract unique categories from resources
        const uniqueCategories = Array.from(
          new Set(resourcesData.map(resource => resource.category))
        ).filter(Boolean); // Filter out any null/undefined/empty categories

        // Assign custom images to specific resources based on name or category
        const enhancedResources = resourcesData.map(resource => {
          let updatedResource = { ...resource };

          // Assign ski gold pass image to resources with 'Gold' in the name or 'Premium' category
          if (
            resource.name.toLowerCase().includes('gold') ||
            resource.name.toLowerCase().includes('premium') ||
            (resource.category && resource.category.toLowerCase().includes('premium'))
          ) {
            updatedResource.imageUrl = '/images/ski-gold-pass.svg';
          }

          // Assign sundance pass image to resources with 'Sundance' in the name
          else if (
            resource.name.toLowerCase().includes('sundance') ||
            (resource.category && resource.category.toLowerCase().includes('sundance'))
          ) {
            updatedResource.imageUrl = '/images/sundance-pass.svg';
          }

          return updatedResource;
        });

        setResources(enhancedResources);
        setFilteredResources(enhancedResources);
        setCategories(uniqueCategories);
      } catch (err) {
        console.error('Error fetching resources:', err);
        setError('Failed to load resources. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Filter resources based on search term and category
    let filtered = resources;

    if (searchTerm) {
      filtered = filtered.filter(resource =>
        resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(resource =>
        resource.category === categoryFilter
      );
    }

    setFilteredResources(filtered);
  }, [searchTerm, categoryFilter, resources]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Resources</Typography>
        {user?.role === 'admin' && (
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/resources/new"
          >
            Add New Resource
          </Button>
        )}
      </Box>

      <Box mb={3} display="flex" gap={2}>
        <TextField
          label="Search Resources"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1 }}
        />

        <TextField
          select
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          variant="outlined"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">All Categories</MenuItem>
          {categories.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {filteredResources.length === 0 ? (
        <Typography>No resources found matching your criteria.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
          {filteredResources.map((resource) => (
            <Card key={resource.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardMedia
                component="img"
                height="140"
                image={resource.imageUrl || 'https://via.placeholder.com/300x140?text=Resource+Image'}
                alt={resource.name}
                sx={{ objectFit: 'contain', bgcolor: '#f5f5f5', p: 1 }}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography gutterBottom variant="h5" component="div">
                    {resource.name}
                  </Typography>
                  <Chip
                    label={resource.isActive ? 'Active' : 'Inactive'}
                    color={resource.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <Chip label={resource.category} size="small" sx={{ mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {resource.description}
                </Typography>
                <Box mt={2}>
                  <Typography variant="body2">
                    Quantity: {resource.quantity}
                  </Typography>
                </Box>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  component={Link}
                  to={`/resources/${resource.id}`}
                >
                  View Details
                </Button>
                <Button
                  size="small"
                  component={Link}
                  to={`/resources/${resource.id}/edit`}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  component={Link}
                  to={`/resources/${resource.id}/reserve`}
                  color="primary"
                >
                  Reserve
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Resources;
